// import { useMemo } from "react";
import { useMemo } from "react";
import cn from "classnames";
import { SeaStatusesModel, seaStatusLabelMap } from "entities/shared/seaCard.types";
import { SeaFilterType } from "features/shared/filter.types";
import { AirFilterType } from "features/shared/filter.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { SealineSelect, useSealinesQuery } from "entities/Sealines";
import { searchFilterStore } from "entities/SearchFilter";
import { SeaOrderQuery, SeaWhereQuery } from "entities/SeaSavedCard";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { useAppSelector } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { Button } from "shared/ui/Button";
import { CustomSelect } from "shared/ui/CustomSelect";
import { FilterOrder } from "./FilterOrder";
import styles from "../Filter.module.scss";

import { useFilterContext } from "../context/Filter.context";

type StatusOptions = {
  value: ValueOf<typeof SeaStatusesModel>;
  label: string;
};

type FilterContentProps = {
  classNames?: string;
  closeFilter: () => void;
};

export const FilterContent: RFC<FilterContentProps> = ({ classNames, closeFilter }) => {
  const { t } = useTranslation();
  const { setFilter, innerFilterState, isSeaFilter, setStatusFilter } = useFilterContext();
  const { showFilteredCardsView } = usePanelViews();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const isAirShipment = ShipmentTypeChecker.isAir(shipment);
  // Sea carrier lines don't apply for shipment-type=air - skip both the select and its query.
  const { seaLineOptions } = useSealinesQuery();

  const isInnerFilterValuesClear = useMemo(
    () => !Object.keys(innerFilterState.filter.where).length && !innerFilterState.filter.order.length,
    [innerFilterState.filter]
  );

  const statusOptions: StatusOptions[] = useMemo(
    () => Object.values(SeaStatusesModel).map((status) => ({ value: status, label: t(seaStatusLabelMap[status]) })),
    []
  );

  return (
    <div className={cn(styles.filterContent, classNames)}>
      <div className={styles.btnContainer}>
        <p>{t("Filters")}</p>
        <div className={styles.btnsWrap}>
          <div
            className={cn(styles.clearBtn)}
            onClick={() => {
              if (!isInnerFilterValuesClear) {
                innerFilterState.resetFilter();
              } else {
                closeFilter();
              }
            }}
          >
            {t("Clear")}
          </div>
          <Button
            variant="primary"
            size="small"
            className={styles.btnSave}
            onClick={() => {
              setFilter(innerFilterState.filter as SeaFilterType & AirFilterType);
              closeFilter();
              showFilteredCardsView();
            }}
          >
            {t("Show")}
          </Button>
        </div>
      </div>

      <FilterOrder
        onChange={(data) => {
          if (isSeaFilter?.(innerFilterState.filter)) {
            (innerFilterState.setOrderQuery as (value: React.SetStateAction<SeaOrderQuery[]>) => void)(
              (prev: SeaOrderQuery[]) => [data[0], ...prev.slice(1)]
            );
          }
        }}
      />

      <div className={styles.separator} />

      <div className={styles.fieldWrap}>
        <div className={styles.field}>
          <div className={cn(styles.propTitle, styles.title)}>{t("Status")}</div>
          <div className={styles.selectWrap}>
            <CustomSelect<StatusOptions>
              placeholder={t("Select")}
              options={statusOptions}
              value={statusOptions.find((option) => option.value === innerFilterState.filter.where.status)}
              handleChange={(value) => {
                innerFilterState.setWhereQuery((prev: SeaWhereQuery) => ({
                  ...prev,
                  status: value.value,
                }));
                setStatusFilter(value.value);
              }}
              componentsClassNames={{
                menu: styles.dropDown,
              }}
            />
          </div>
        </div>

        {!isAirShipment && (
          <div className={styles.field}>
            <div className={cn(styles.propTitle, styles.title)}>{t("Carrier")}</div>

            <div className={styles.selectWrap}>
              {seaLineOptions.length > 0 && (
                <SealineSelect
                  value={innerFilterState.filter.where.carrier_scac}
                  menuPlacement="bottom"
                  componentsClassNames={{
                    menu: styles.dropDown,
                  }}
                  onChange={(value) => {
                    innerFilterState.setWhereQuery((prev: SeaWhereQuery) => ({
                      ...prev,
                      carrier_scac: value.value,
                    }));
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
